
import React, { useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthProvider';
import { useOpportunities } from '../contexts/OpportunitiesProvider';
import { useNavigation } from '../contexts/NavigationProvider';
import PostCard from './PostCard';
import SearchIcon from './icons/SearchIcon';
import UsersIcon from './icons/UsersIcon';
import BriefcaseIcon from './icons/BriefcaseIcon';
import MapPinIcon from './icons/MapPinIcon';
import { USER_ROLES, OPPORTUNITY_TYPES, UserRole, User, Band } from '../types';
import { FilterDropdown } from './SearchResultsView';

const CLUSTERS = ['talent', 'business', 'service', 'edu'];
const COMMON_INSTRUMENTS = ['Guitar', 'Bass', 'Drums', 'Vocals', 'Keyboard', 'Piano', 'Saxophone', 'Trumpet', 'Violin', 'DJ', 'Percussion'];
const COMMON_GENRES = ['Rock', 'Pop', 'Jazz', 'Blues', 'Electronic', 'Hip-Hop', 'Classical', 'Folk', 'Metal', 'R&B', 'Soul', 'Funk'];

// Mapping clusters to roles for cross-cluster filtering (including multi-cluster users)
const CLUSTER_ROLE_MAP: Record<string, string[]> = {
    talent: ['Musician', 'Singer', 'DJ', 'Actor', 'Director', 'Dancer', 'Performer', 'Choreographer', 'Model', 'Comedian', 'Writer', 'Painter', 'Visual Artist'],
    business: ['Venue', 'Live Club', 'Theatre', 'Cinema', 'Festival', 'Label', 'Radio', 'Movie/TV Production', 'Casting Agency', 'Event Agency', 'Publisher', 'Producer'],
    service: ['Audio Service', 'Recording Studio', 'Music Store', 'Luthier', 'Sound Engineer', 'Light Designer', 'Filmmaker / Videographer', 'Photographer', 'Make-Up Artist (MUA)', 'Costume Designer / Stylist', 'Backline Rental', 'Graphic Designer'],
    edu: ['Music School', 'Acting Academy', 'Dance School', 'Cultural Association', 'Vocal Coach'],
};

// Normalize common city duplicates (Legacy Support)
const normalizeGeo = (val?: string) => {
    if (!val) return '';
    const map: Record<string, string> = { 'Roma': 'Rome', 'Milano': 'Milan', 'Napoli': 'Naples', 'Firenze': 'Florence' };
    return map[val] || val;
};

const SearchPage: React.FC = () => {
    const { users, hasMoreUsers, isLoadingMoreUsers, loadMoreUsers } = useAuth();
    const { opportunities, hasMore, isLoadingMore, loadMoreOpportunities } = useOpportunities();
    const { handleViewProfile, handleViewBand, bands, hasMoreBands, isLoadingMoreBands, loadMoreBands } = useNavigation();

    const [mode, setMode] = useState<'users' | 'gigs' | 'bands'>('users');
    const [query, setQuery] = useState('');
    
    // Shared Filters
    const [selectedCities, setSelectedCities] = useState<string[]>([]);
    const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
    const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
    const [selectedInstruments, setSelectedInstruments] = useState<string[]>([]);
    const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
    
    // Mode specific filters
    const [selectedClusters, setSelectedClusters] = useState<string[]>([]);
    const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
    const [selectedOppTypes, setSelectedOppTypes] = useState<string[]>([]);

    // Geo options from all sources with deduplication logic
    const geoOptions = useMemo(() => {
        const cities = new Set<string>();
        const regions = new Set<string>();
        const countries = new Set<string>();
        const processItem = (loc?: string, reg?: string, cntry?: string) => {
          if (loc) cities.add(normalizeGeo(loc.split(',')[0].trim()));
          if (reg) regions.add(reg.trim());
          if (cntry) countries.add(cntry.trim());
        };
        opportunities.forEach(o => processItem(o.location, o.region, o.country));
        users.forEach(u => processItem(u.city, u.region, u.country));
        bands.forEach(b => processItem(b.city, b.region, b.country));
        return {
          cities: Array.from(cities).filter(Boolean).sort(),
          regions: Array.from(regions).filter(Boolean).sort(),
          countries: Array.from(countries).filter(Boolean).sort(),
        };
    }, [opportunities, users, bands]);

    const filteredUsers = useMemo(() => {
        return users.filter(u => {
            const userRoles = u.roles || (u.role ? [u.role] : []);
            
            const matchQuery = !query || u.name.toLowerCase().includes(query.toLowerCase()) || u.handle.toLowerCase().includes(query.toLowerCase());
            
            // Enhanced Cluster Filter: Logic that includes multi-cluster users if they have roles in the selected cluster
            const matchCluster = selectedClusters.length === 0 || selectedClusters.some(c => {
                // 1. Direct match with user's primary tier
                if (u.subscriptionTier?.startsWith(c)) return true;
                
                // 2. Multi-cluster logic: if user is multi, check if their roles intersect with the target cluster
                if (u.subscriptionTier?.startsWith('multi')) {
                    const rolesForSelectedCluster = CLUSTER_ROLE_MAP[c] || [];
                    return userRoles.some(r => rolesForSelectedCluster.includes(r));
                }
                
                return false;
            });

            const matchRoles = selectedRoles.length === 0 || selectedRoles.some(r => userRoles.includes(r as UserRole));
            
            const uCityNormalized = normalizeGeo(u.city?.split(',')[0].trim());
            const matchCity = selectedCities.length === 0 || (uCityNormalized && selectedCities.includes(uCityNormalized));
            const matchRegion = selectedRegions.length === 0 || (u.region && selectedRegions.includes(u.region));
            const matchCountry = selectedCountries.length === 0 || (u.country && selectedCountries.includes(u.country));
            
            const matchInstr = selectedInstruments.length === 0 || selectedInstruments.some(i => u.instruments?.includes(i));
            const matchGenres = selectedGenres.length === 0 || selectedGenres.some(g => u.genres?.includes(g));

            return matchQuery && matchCluster && matchRoles && matchCity && matchRegion && matchCountry && matchInstr && matchGenres;
        });
    }, [users, query, selectedClusters, selectedRoles, selectedCities, selectedRegions, selectedCountries, selectedInstruments, selectedGenres]);

    const filteredOpps = useMemo(() => {
        return opportunities.filter(o => {
            const matchQuery = !query || o.title.toLowerCase().includes(query.toLowerCase()) || o.description.toLowerCase().includes(query.toLowerCase());
            const matchTypes = selectedOppTypes.length === 0 || selectedOppTypes.includes(o.type);
            
            const oCityNormalized = normalizeGeo(o.location.split(',')[0].trim());
            const matchCity = selectedCities.length === 0 || (oCityNormalized && selectedCities.includes(oCityNormalized));
            const matchRegion = selectedRegions.length === 0 || (o.region && selectedRegions.includes(o.region));
            const matchCountry = selectedCountries.length === 0 || (o.country && selectedCountries.includes(o.country));

            const matchInstr = selectedInstruments.length === 0 || (o.instrument && selectedInstruments.some(i => o.instrument?.toLowerCase().includes(i.toLowerCase())));
            const matchGenres = selectedGenres.length === 0 || (o.genre && selectedGenres.some(g => o.genre?.toLowerCase().includes(g.toLowerCase())));

            return matchQuery && matchTypes && matchCity && matchRegion && matchCountry && matchInstr && matchGenres;
        });
    }, [opportunities, query, selectedOppTypes, selectedCities, selectedRegions, selectedCountries, selectedInstruments, selectedGenres]);

    const filteredBands = useMemo(() => {
        return bands.filter(b => {
            const matchQuery = !query || b.name.toLowerCase().includes(query.toLowerCase()) || (b.bio && b.bio.toLowerCase().includes(query.toLowerCase()));
            
            const bCityNormalized = normalizeGeo(b.city?.split(',')[0].trim());
            const matchCity = selectedCities.length === 0 || (bCityNormalized && selectedCities.includes(bCityNormalized));
            const matchRegion = selectedRegions.length === 0 || (b.region && selectedRegions.includes(b.region));
            const matchCountry = selectedCountries.length === 0 || (b.country && selectedCountries.includes(b.country));

            const matchInstr = selectedInstruments.length === 0 || selectedInstruments.some(i => b.instruments?.includes(i));
            const matchGenres = selectedGenres.length === 0 || selectedGenres.some(g => b.genres?.includes(g));

            return matchQuery && matchCity && matchRegion && matchCountry && matchInstr && matchGenres;
        });
    }, [bands, query, selectedCities, selectedRegions, selectedCountries, selectedInstruments, selectedGenres]);

    const clearFilters = () => {
        setSelectedCities([]); setSelectedRegions([]); setSelectedCountries([]);
        setSelectedInstruments([]); setSelectedGenres([]); setSelectedClusters([]);
        setSelectedRoles([]); setSelectedOppTypes([]);
    };

    return (
        <div className="space-y-6 font-urbanist pb-10">
            <div className="bg-brand-surface rounded-xl p-6 border border-gray-800 shadow-xl">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
                    <div>
                        <h2 className="text-3xl font-black text-white tracking-tighter uppercase">Global Explorer</h2>
                        <p className="text-brand-muted text-sm uppercase font-bold tracking-widest mt-1">Discover users and market opportunities</p>
                    </div>
                    
                    <div className="flex flex-wrap bg-gray-800 p-1.5 rounded-2xl border border-gray-700 w-full md:w-auto gap-1">
                        <button onClick={() => { setMode('users'); clearFilters(); }} className={`flex-1 md:w-32 py-2.5 px-4 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${mode === 'users' ? 'bg-brand-orange text-white shadow-lg' : 'text-brand-muted hover:text-white hover:bg-gray-700/50'}`}>
                            <UsersIcon className="h-4 w-4" /> Users
                        </button>
                        <button onClick={() => { setMode('bands'); clearFilters(); }} className={`flex-1 md:w-32 py-2.5 px-4 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${mode === 'bands' ? 'bg-brand-orange text-white shadow-lg' : 'text-brand-muted hover:text-white hover:bg-gray-700/50'}`}>
                            <UsersIcon className="h-4 w-4" /> Projects
                        </button>
                        <button onClick={() => { setMode('gigs'); clearFilters(); }} className={`flex-1 md:w-32 py-2.5 px-4 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${mode === 'gigs' ? 'bg-brand-orange text-white shadow-lg' : 'text-brand-muted hover:text-white hover:bg-gray-700/50'}`}>
                            <BriefcaseIcon className="h-4 w-4" /> Market
                        </button>
                    </div>
                </div>

                <div className="relative mb-8">
                    <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-brand-orange" />
                    <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder={mode === 'gigs' ? "Search keywords, roles..." : "Search names, handles..."} className="w-full bg-gray-700/50 border border-gray-600 text-white rounded-xl py-4 pl-12 pr-4 focus:ring-2 focus:ring-brand-orange outline-none font-bold" />
                </div>

                <div className="flex flex-wrap gap-3">
                    {mode === 'users' && (
                        <>
                            <FilterDropdown label="Cluster" options={CLUSTERS} selected={selectedClusters} onToggle={(v) => setSelectedClusters(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v])} />
                            <FilterDropdown label="Role" options={USER_ROLES} selected={selectedRoles} onToggle={(v) => setSelectedRoles(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v])} />
                        </>
                    )}
                    {mode === 'gigs' && (
                        <FilterDropdown label="Type" options={OPPORTUNITY_TYPES} selected={selectedOppTypes} onToggle={(v) => setSelectedOppTypes(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v])} />
                    )}
                    
                    <FilterDropdown label="Instrument" options={COMMON_INSTRUMENTS} selected={selectedInstruments} onToggle={(v) => setSelectedInstruments(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v])} />
                    <FilterDropdown label="Genre" options={COMMON_GENRES} selected={selectedGenres} onToggle={(v) => setSelectedGenres(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v])} />
                    
                    <div className="w-full h-px bg-gray-800 my-1" />
                    
                    <FilterDropdown label="City" options={geoOptions.cities} selected={selectedCities} onToggle={(v) => setSelectedCities(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v])} icon={<MapPinIcon className="h-3 w-3" />} />
                    <FilterDropdown label="Region" options={geoOptions.regions} selected={selectedRegions} onToggle={(v) => setSelectedRegions(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v])} icon={<MapPinIcon className="h-3 w-3" />} />
                    <FilterDropdown label="Country" options={geoOptions.countries} selected={selectedCountries} onToggle={(v) => setSelectedCountries(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v])} icon={<MapPinIcon className="h-3 w-3" />} />
                </div>
            </div>

            <div className="flex items-center justify-between px-2">
                <h3 className="text-xs font-black text-brand-muted uppercase tracking-[0.3em]">
                    {mode === 'users' ? `Matching Users (${filteredUsers.length})` : mode === 'bands' ? `Matching Projects (${filteredBands.length})` : `Open Calls (${filteredOpps.length})`}
                </h3>
                <button onClick={clearFilters} className="text-[10px] font-black text-brand-orange uppercase tracking-widest hover:underline">Clear Filters</button>
            </div>

            <div className="space-y-4">
                {mode === 'users' ? (
                    <>
                        {filteredUsers.length > 0 ? filteredUsers.map(u => (
                            <UserResultCard key={u.handle} user={u} onSelect={() => handleViewProfile(u)} />
                        )) : <NoResults />}
                        {hasMoreUsers && (
                            <button
                                onClick={loadMoreUsers}
                                disabled={isLoadingMoreUsers}
                                className="w-full py-3 bg-gray-800 text-brand-muted font-semibold rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 font-urbanist"
                            >
                                {isLoadingMoreUsers ? 'Loading...' : 'Load more users'}
                            </button>
                        )}
                    </>
                ) : mode === 'bands' ? (
                    <>
                        {filteredBands.length > 0 ? filteredBands.map(b => (
                            <BandResultCard key={b.id} band={b} onSelect={() => handleViewBand(b)} />
                        )) : <NoResults />}
                        {hasMoreBands && (
                            <button
                                onClick={loadMoreBands}
                                disabled={isLoadingMoreBands}
                                className="w-full py-3 bg-gray-800 text-brand-muted font-semibold rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 font-urbanist"
                            >
                                {isLoadingMoreBands ? 'Loading...' : 'Load more bands'}
                            </button>
                        )}
                    </>
                ) : (
                    <>
                        {filteredOpps.length > 0 ? filteredOpps.map(o => (
                            <PostCard key={o.id} opportunity={o} />
                        )) : <NoResults />}
                        {hasMore && (
                            <button
                                onClick={loadMoreOpportunities}
                                disabled={isLoadingMore}
                                className="w-full py-3 bg-gray-800 text-brand-muted font-semibold rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 font-urbanist"
                            >
                                {isLoadingMore ? 'Loading...' : 'Load more opportunities'}
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

const UserResultCard: React.FC<{ user: User, onSelect: () => void }> = ({ user, onSelect }) => {
    const geo = [user.city, user.region, user.country].filter(Boolean).join(', ');
    return (
        <div onClick={onSelect} className="bg-brand-surface p-5 rounded-xl border border-gray-800 hover:border-brand-orange transition-all cursor-pointer flex items-center gap-5 group">
            <img src={user.avatarUrl} className="h-16 w-16 rounded-full border-2 border-gray-700 group-hover:border-brand-orange transition-colors" alt="" />
            <div className="flex-1 min-w-0">
                <p className="font-black text-white text-lg truncate tracking-tight group-hover:text-brand-orange transition-colors">{user.name}</p>
                <p className="text-xs text-brand-orange font-bold tracking-tighter">{user.title}</p>
                <div className="flex items-center text-brand-muted mt-2 gap-3">
                    <span className="text-[10px] font-bold font-mono">@{user.handle}</span>
                    <span className="text-[10px] bg-gray-800 px-2 py-0.5 rounded text-gray-300 font-black uppercase tracking-widest truncate">{geo}</span>
                </div>
            </div>
        </div>
    );
};

const BandResultCard: React.FC<{ band: Band, onSelect: () => void }> = ({ band, onSelect }) => {
    const geo = [band.city, band.region, band.country].filter(Boolean).join(', ');
    return (
        <div onClick={onSelect} className="bg-brand-surface p-5 rounded-xl border border-gray-800 hover:border-brand-orange transition-all cursor-pointer flex items-center gap-5 group">
            <img src={band.avatarUrl} className="h-16 w-16 rounded-lg border-2 border-gray-700 group-hover:border-brand-orange transition-colors object-cover" alt="" />
            <div className="flex-1 min-w-0">
                <p className="font-black text-white text-lg truncate tracking-tight group-hover:text-brand-orange transition-colors">{band.name}</p>
                <p className="text-xs text-brand-muted font-bold uppercase tracking-widest">{band.members.length} Members</p>
                <div className="flex items-center text-brand-muted mt-2 gap-3">
                    <span className="text-[10px] bg-gray-800 px-2 py-0.5 rounded text-gray-300 font-black uppercase tracking-widest truncate">{geo}</span>
                </div>
            </div>
        </div>
    );
};

const NoResults = () => (
    <div className="bg-brand-surface rounded-2xl p-16 text-center border-2 border-dashed border-gray-800">
        <SearchIcon className="h-12 w-12 text-gray-700 mx-auto mb-4" />
        <p className="text-brand-muted font-bold uppercase tracking-widest">No entries match your current stage settings.</p>
    </div>
);

export default SearchPage;
